import {
	Box,
	IconButton,
	Pagination,
	Stack,
	Typography,
	Divider,
	FormControlLabel,
	Checkbox,
} from '@mui/material';
import CustomButton from '@src/components/CustomButton/CustomButton';
import CustomDataGrid2 from '@src/components/CustomDataGrid/CustomDataGrid2';
import { IsMobileWidth, IsTabletWidth } from '@src/components/MediaQueryUtils';
import PageHeader from '@src/components/PageHeader/PageHeader';
import SearchBar from '@src/components/SearchBar';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import CustomAlert from '@src/components/CustomAlert/CustomAlert';
import { roundTo2Decimals } from '@src/helpers/Helpers';
import CustomDrawer from '@src/components/CustomDrawer/CustomDrawer';
import LabeledTextField from '@src/components/CustomTextField/LabeledTextField';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	background-color: var(--chat-header-bg);
	transition: width 0.3s ease;
`;

const ExportButtonStyles = styled('div')({
	'@media (max-width: 1024px)': {
		marginLeft: '16px',
	},
	'@media (max-width: 478px)': {
		background: 'white',
		display: 'flex',
		marginLeft: '0px',
		position: 'fixed',
		bottom: 0,
		zIndex: '100',
		width: '100%',
		padding: '16px',
		left: 0,
		borderRadius: '6px 6px 0px 0px',
		borderTop: '1.5px solid #F1F1F1',
	},
});

const getColumns = (onEditPressed: any) => [
	{
		field: 'organization_name',
		headerName: 'Dealer',
		width: 200,
		renderCell: (params: any) => (
			<Stack direction="row" alignItems={'center'} gap={1}>
				{params?.row?.image && (
					<img
						src={params?.row?.image}
						alt="organization-logo"
						width="30px"
						height="30px"
						style={{ borderRadius: '50%' }}
					/>
				)}
				{params?.row?.name}
			</Stack>
		),
	},
	{
		field: 'name',
		headerName: 'Contact Name',
		width: 200,
		renderCell: (params: any) => (
			<div>{params?.row?.customers?.firstname + ' ' + params?.row?.customers?.lastname}</div>
		),
	},
	{
		field: 'contact_no',
		headerName: 'Contact No',
		width: 200,
		renderCell: (params: any) => <div>{params?.row?.customers?.telephone}</div>,
	},
	{
		field: 'credit_limit',
		headerName: 'Credit Limit',
		width: 200,
		renderCell: (params: any) => <div>Rs {params?.row?.restaurant_credit_limit?.credit_limit}</div>,
	},
	{
		field: 'pending',
		headerName: 'Pending',
		width: 200,
		renderCell: (params: any) => <div>Rs {params?.row?.restaurant_credit_limit?.pending}</div>,
	},
	{
		field: 'remaining',
		headerName: 'Remaining',
		width: 200,
		renderCell: (params: any) => {
			const pending = params?.row?.restaurant_credit_limit?.pending;
			const creditLimit = params?.row?.restaurant_credit_limit?.credit_limit;
			const remaining = creditLimit - pending;
			if (remaining < 0) {
				return <div style={{ color: 'red' }}>Over Limit</div>;
			}
			return <div>Rs {roundTo2Decimals(remaining)}</div>;
		},
	},
	{
		field: 'actions',
		headerName: 'Actions',
		width: 150,
		renderCell: (params: any) => {
			return (
				<Stack direction={'row'} gap={1} alignItems={'center'}>
					<IconButton onClick={(e) => onEditPressed(e, params.row)}>
						<EditOutlinedIcon />
					</IconButton>
				</Stack>
			);
		},
	},
];

function RenderCreditSettings({
	showCustomerCreditSettings,
	creditDetails,
	setShowCustomerCredit,
	currentCustomer,
	updateCreditSettings,
	isMobile,
}: any) {
	const [creditLimit, setCreditLimit] = useState(0);
	const [allowNewOrders, setAllowNewOrders] = useState(true);

	const { restaurant } = creditDetails || {};
	const { customers } = currentCustomer || {};
	const { telephone } = customers || {};

	useEffect(() => {
		if (creditDetails) {
			if (creditDetails?.creditLimit) {
				setCreditLimit(creditDetails?.creditLimit);
			}
			setAllowNewOrders(creditDetails?.allowNewOrders);
		}
	}, [creditDetails]);

	if (!showCustomerCreditSettings || !creditDetails) return null;

	return (
		<CustomDrawer
			open={showCustomerCreditSettings}
			style={{ width: '80%' }}
			setOpen={setShowCustomerCredit}
			header={false}
		>
			<Box m={1}>
				<Typography sx={{ mt: 2, mb: 2 }} fontSize={20}>
					Credit Settings for {currentCustomer?.name}
				</Typography>
				<Divider />
				<Stack
					direction="row"
					alignItems={'center'}
					gap={1}
					p={1}
					sx={{
						backgroundColor: 'ghostwhite',
					}}
				>
					<img
						src={currentCustomer?.image || '/Cheeni-fallback.svg'}
						alt="organization-logo"
						width={isMobile ? '100px' : '150px'}
						height={isMobile ? '100px' : '150px'}
						style={{ borderRadius: '50%' }}
					/>
					<Stack>
						<Typography fontSize={20} fontWeight={600}>
							{restaurant?.name}
						</Typography>
						<Typography fontSize={16}>{restaurant?.address}</Typography>
						<Typography fontSize={16}>Phone No: {telephone}</Typography>
					</Stack>
				</Stack>
				<Stack mt={2} p={2}>
					<Stack direction={'row'} justifyContent={'space-between'}>
						<Typography fontSize={16} fontWeight={600}>
							Credit Limit
						</Typography>
						<CustomButton
							title={'Update'}
							styles={{ height: '33px', marginBottom: '3px' }}
							handleClick={() =>
								updateCreditSettings({
									creditLimit,
									allowNewOrders,
								})
							}
						/>
					</Stack>
					<Stack flex={1}>
						<LabeledTextField
							label="Credit Limit"
							name="credit_limit"
							value={creditLimit}
							onChange={(e) => setCreditLimit(e.target.value)}
							styles={{ width: '300px', height: '36px' }}
							placeholder="Eg 1000"
							// errorText={errors?.maxRewardAmountPerOrder}
						/>
					</Stack>
					<Stack flex={1}>
						<FormControlLabel
							control={
								<Checkbox
									disabled={true}
									onChange={(e) => setAllowNewOrders(!e.target.checked)}
									checked={!allowNewOrders}
								/>
							}
							label="Dont allow customer to submit new orders when credit limit is exceeded"
						/>
					</Stack>
				</Stack>
			</Box>
		</CustomDrawer>
	);
}

function CreditLimit() {
	const { id } = useParams();
	const navigate = useNavigate();
	const isMobile = IsMobileWidth();
	const [searchValue, setSearchValue] = useState('');
	const [totalPages, setTotalPages] = useState(1);
	const [page, setPage] = useState(1);
	const [searching, setSearching] = useState(false);
	const [showCustomerCreditSettings, setShowCustomerCredit] = useState(false);
	const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
	const [shareModal, setShareModal] = useState(false);
	const [loadingCustomerList, setLoadingCustomerList] = useState<boolean>(false);
	const [customerList, setCustomerList] = useState([]);
	const { apiService }: any = React.useContext(ApplicationContext);
	const [creditDetails, setCreditDetails] = useState(null);
	const [currentCustomer, setCurrentCustomer] = useState({});
	const [alert, setAlert] = useState({
		message: '',
		severity: 'success',
	});
	const limit = 25;

	let params = {
		limit: limit,
		page: page,
		search: searchValue,
	};

	const onEditPressed = (e: any, data: any) => {
		e.preventDefault();
		setCurrentCustomer(data);
		fetchCustomerCreditDetails(data?.id);
		setShowCustomerCredit(true);
	};

	const columns = getColumns(onEditPressed);
	const handleSearch = (value: any) => {
		setSearching(true);
		setSearchValue(value);
		setPage(1);
	};

	const handlePageChange = (event: any, value: number) => {
		if (page !== value) {
			setPage(value);
			setSelectedItemIds([]);
			setLoadingCustomerList(true);
		}
	};

	const onRowsSelectionHandler = (ids: string[]) => {
		setSelectedItemIds(ids);
	};

	useEffect(() => {
		fetchCustomerList();
	}, [searchValue]);

	function fetchCustomerList() {
		setLoadingCustomerList(true);
		apiService.getCustomerList(
			(response: any) => {
				// console.log(response);
				const data = response?.data?.data;
				const { totalCount, customers } = data || {};
				setCustomerList(customers);
				setTotalPages(Math.ceil(totalCount / limit));
				setLoadingCustomerList(false);
			},
			(error: any) => {
				// console.log(error);
				setAlert({ message: error.response.data.message, severity: 'error' });
				setLoadingCustomerList(false);
			},
			id,
			params
		);
	}

	function fetchCustomerCreditDetails(customerId: any) {
		setLoadingCustomerList(true);
		apiService.getCreditLimit(
			(response: any) => {
				// console.log(response);
				const data = response?.data?.data?.data;
				setCreditDetails(data);
			},
			(error: any) => {
				// console.log(error);
				setAlert({ message: error.response.data.message, severity: 'error' });
				setLoadingCustomerList(false);
			},
			customerId
		);
	}

	useEffect(() => {
		fetchCustomerList();
	}, []);

	const updateCreditSettings = (creditDetails: any) => {
		apiService.updateCreditLimit(
			(response: any) => {
				// console.log(response);
				setAlert({ message: 'Credit limit updated successfully', severity: 'success' });
				setShowCustomerCredit(false);
				fetchCustomerList();
			},
			(error: any) => {
				// console.log(error);
				setAlert({ message: error.response.data.message, severity: 'error' });
			},
			currentCustomer?.id,
			creditDetails
		);
	};

	return (
		<StyledContainer>
			<PageHeader title={'Credit Limits'} />
			<Stack p={2} direction="row" justifyContent="space-between">
				<SearchBar
					style={{
						width: IsTabletWidth() ? '100%' : '312px',
						height: '36px',
					}}
					value={searchValue}
					onChange={handleSearch}
					isLoading={false}
				/>
				{/* <ExportButtonStyles>
					<CustomButton
						title="Export"
						color="primary"
						styles={{
							height: '36px',
							'@media (max-width: 478px)': {
								width: '100%',
							},
						}}
						disabled={!selectedItemIds?.length}
						handleClick={() => setShareModal(true)}
					/>
				</ExportButtonStyles> */}
			</Stack>
			<Box
				m={1}
				sx={{ width: isMobile ? 'calc(100vw - 10px)' : 'calc(100vw - 258px)', overflowX: 'auto' }}
			>
				<CustomDataGrid2
					rows={customerList}
					columns={columns}
					style={{
						transition: 'height 0.3s ease',
						maxHeight: 'calc(100vh - 250px)',
						minHeight: 'calc(100vh - 250px)',
					}}
					// showNoRowsFound={!orders?.length}
					onRowsSelectionHandler={onRowsSelectionHandler}
					handleRowClick={() => {}}
					// responsiveStyles={gridResponsiveStyles}
					// loading={loadingOrders}
					type="server"
					filterType="server"
				/>
				<Stack alignItems={'center'} mt={1}>
					<Pagination color="primary" count={totalPages} page={page} onChange={handlePageChange} />
				</Stack>
			</Box>
			<RenderCreditSettings
				showCustomerCreditSettings={showCustomerCreditSettings}
				setShowCustomerCredit={setShowCustomerCredit}
				currentCustomer={currentCustomer}
				creditDetails={creditDetails}
				updateCreditSettings={updateCreditSettings}
				isMobile={isMobile}
			/>
			{alert?.message !== '' ? <CustomAlert alert={alert} setAlert={setAlert} /> : null}
		</StyledContainer>
	);
}

export default CreditLimit;
